var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { createDemoPeriodComparisonMatcher } from '@web/lib/demo/utils';
import { AnalyticPeriodsEnum, GeminiAppNameEnum } from '@workalizer/shared';
import { formatDuration, getInterval, getPercentageDiff, getPreviousPeriod, groupByPeriod, randomInt } from '@workalizer/shared/utils';
import { buildChartAppNames } from '@workalizer/shared/utils/geminiInvocationsChart';
import { endOfDay, format, startOfDay } from 'date-fns';
var createItem = function (_a) {
    var appName = _a.appName, invocations = _a.invocations, invocationsPrevDiff = _a.invocationsPrevDiff, invocationsTeamDiff = _a.invocationsTeamDiff, minutes = _a.minutes, timePrevDiff = _a.timePrevDiff, timeTeamDiff = _a.timeTeamDiff;
    return ({
        appName: appName,
        invocations: {
            count: invocations,
            prevPeriodDiff: invocationsPrevDiff,
            teamAvgDiff: invocationsTeamDiff
        },
        timeSpent: {
            formatted: formatDuration(minutes / 60),
            minutes: minutes,
            prevPeriodDiff: timePrevDiff,
            teamAvgDiff: timeTeamDiff
        }
    });
};
export var invocationsByApp = function (input) {
    var showTeamDiffs = Boolean(input.userId);
    var interval = getInterval(input.dateFrom, input.dateTo);
    var periods = groupByPeriod([], startOfDay(input.dateFrom), endOfDay(input.dateTo), interval);
    var _a = getPreviousPeriod(input.dateFrom, input.dateTo), prevPeriodDateFrom = _a.prevPeriodDateFrom, prevPeriodDateTo = _a.prevPeriodDateTo;
    var periodsPreviousWindow = groupByPeriod([], startOfDay(prevPeriodDateFrom), endOfDay(prevPeriodDateTo), interval);
    var mockCurrentPeriodData = periods.map(function () {
        var record = {};
        Object.values(GeminiAppNameEnum).forEach(function (appName) {
            record[appName] = randomInt(0, 40);
        });
        return record;
    });
    var mockPreviousPeriodData = periods.map(function (_, idx) {
        var record = {};
        var currentData = mockCurrentPeriodData[idx];
        Object.values(GeminiAppNameEnum).forEach(function (appName) {
            record[appName] = Math.max(currentData[appName] + randomInt(-15, 20), 0);
        });
        return record;
    });
    var mockTeamPeriodData = periods.map(function (_, idx) {
        var record = {};
        var currentData = mockCurrentPeriodData[idx];
        Object.values(GeminiAppNameEnum).forEach(function (appName) {
            record[appName] = showTeamDiffs
                ? Math.max(currentData[appName] + randomInt(-10, 15), 0)
                : 0;
        });
        return record;
    });
    var periodMatcher = createDemoPeriodComparisonMatcher(input, interval, periods, periodsPreviousWindow);
    var periodsData = periods.map(function (period, idx) {
        var periodLabel = interval === AnalyticPeriodsEnum.DAY
            ? format(new Date(period.date), 'MMM d')
            : period.date;
        var prevPeriod = periodMatcher.getItem(idx, periodsPreviousWindow);
        var previousIndex = prevPeriod
            ? periodsPreviousWindow.indexOf(prevPeriod)
            : null;
        var previousPeriodLabel = prevPeriod
            ? interval === AnalyticPeriodsEnum.DAY
                ? format(new Date(prevPeriod.date), 'MMM d')
                : prevPeriod.date
            : '';
        var result = {
            period: periodLabel,
            previousPeriodLabel: previousPeriodLabel,
            currentPeriod: mockCurrentPeriodData[idx],
            previousPeriod: previousIndex != null
                ? mockPreviousPeriodData[previousIndex]
                : mockPreviousPeriodData[idx]
        };
        if (showTeamDiffs) {
            result.team = mockTeamPeriodData[idx];
        }
        return result;
    });
    var items = [];
    var totalAcc = {
        invocations: 0,
        previousInvocations: 0,
        teamInvocations: 0,
        minutes: 0,
        previousMinutes: 0,
        teamMinutes: 0
    };
    Object.values(GeminiAppNameEnum).forEach(function (appName) {
        var appTotals = periodsData.reduce(function (acc, period) {
            var _a, _b;
            acc.current += period.currentPeriod[appName];
            acc.previous += period.previousPeriod[appName];
            acc.team += (_b = (_a = period.team) === null || _a === void 0 ? void 0 : _a[appName]) !== null && _b !== void 0 ? _b : 0;
            return acc;
        }, { current: 0, previous: 0, team: 0 });
        var invocations = appTotals.current;
        var previousInvocations = appTotals.previous;
        var teamAverageInvocations = showTeamDiffs
            ? Math.round(appTotals.team / periodsData.length)
            : 0;
        var minutes = invocations > 0 ? randomInt(invocations * 2, invocations * 6) : 0;
        var previousMinutes = previousInvocations > 0
            ? randomInt(previousInvocations * 2, previousInvocations * 6)
            : 0;
        var teamAverageMinutes = showTeamDiffs && teamAverageInvocations > 0
            ? randomInt(teamAverageInvocations * 2, teamAverageInvocations * 6)
            : 0;
        var invocationsPrevDiff = getPercentageDiff(previousInvocations, invocations);
        var timePrevDiff = getPercentageDiff(previousMinutes, minutes);
        var invocationsTeamDiff = showTeamDiffs
            ? getPercentageDiff(teamAverageInvocations, invocations)
            : null;
        var timeTeamDiff = showTeamDiffs
            ? getPercentageDiff(teamAverageMinutes, minutes)
            : null;
        if (invocations === 0) {
            return;
        }
        totalAcc.invocations += invocations;
        totalAcc.previousInvocations += previousInvocations;
        totalAcc.teamInvocations += appTotals.team;
        totalAcc.minutes += minutes;
        totalAcc.previousMinutes += previousMinutes;
        totalAcc.teamMinutes += teamAverageMinutes;
        items.push(createItem({
            appName: appName,
            invocations: invocations,
            invocationsPrevDiff: invocationsPrevDiff,
            invocationsTeamDiff: invocationsTeamDiff,
            minutes: minutes,
            timePrevDiff: timePrevDiff,
            timeTeamDiff: timeTeamDiff
        }));
    });
    var total = totalAcc.invocations === 0
        ? null
        : (function () {
            var _a = createItem({
                appName: GeminiAppNameEnum.GMAIL,
                invocations: totalAcc.invocations,
                invocationsPrevDiff: getPercentageDiff(totalAcc.previousInvocations, totalAcc.invocations),
                invocationsTeamDiff: showTeamDiffs
                    ? getPercentageDiff(Math.round(totalAcc.teamInvocations / periodsData.length), totalAcc.invocations)
                    : null,
                minutes: totalAcc.minutes,
                timePrevDiff: getPercentageDiff(totalAcc.previousMinutes, totalAcc.minutes),
                timeTeamDiff: showTeamDiffs
                    ? getPercentageDiff(Math.round(totalAcc.teamMinutes / periodsData.length), totalAcc.minutes)
                    : null
            }), _appName = _a.appName, metrics = __rest(_a, ["appName"]);
            return metrics;
        })();
    return {
        items: items,
        total: total,
        periods: periodsData,
        chartAppNames: buildChartAppNames(periodsData)
    };
};
